/**
 * The Autodesk daily report, as an editor ("Led AI - Daily Report Editor").
 *
 * One Form per project per local day, built from the reviewed records of
 * every walk that day. The report is drawn from the Form's FROZEN layout
 * (`template.plan`), never the project's current template, and its values
 * come from Led AI: the records behind each table row and the day-level
 * overrides a reviewer typed here. The backend re-projects the whole day on
 * every publish, which is why an edit here never writes Autodesk directly:
 *
 * - a table cell edits its record (`postItemEdit`), a new row is a new
 *   reviewed record (`postAutodeskReportRow`) and removing a row rejects it;
 * - the title, description, notes, location, a question's answer and the
 *   Form's photos are overrides the publisher keeps
 *   (`postAutodeskReportOverride`).
 *
 * Autodesk supplies what Led AI never writes: the weather and the
 * signature, read from the saved Form.
 *
 * Saving (Led AI has the edit) and syncing (Autodesk has it) are separate
 * states, and the badge says which one the report is in.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, FocusEvent, ReactNode } from "react";
import {
  MinusCircle,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleAlert,
  Cloud,
  CloudDownload,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  ExternalLink,
  Footprints,
  Loader,
  MapPin,
  Plus,
  RefreshCw,
  Sun,
  Trash2,
} from "lucide-react";

import type { AutodeskSavedForm, AutodeskSavedValue } from "../../../../backend/src/shared/autodeskSavedForm";
import type {
  AutodeskDailyLogDisplayGroup,
  AutodeskTemplateSectionSnapshot,
} from "../../../../backend/src/shared/autodeskFormTemplate";
import {
  PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS,
  type ProcoreDailyLogCategory,
} from "../../../../backend/src/shared/procoreDailyLog";
import {
  fetchAutodeskReport,
  fetchItemEditOptions,
  fetchWalkDetail,
  postAutodeskReportOverride,
  postAutodeskReportRow,
  postItemEdit,
  postReviewDecision,
  type AutodeskReportOverridePatch,
  type ConsoleRow,
  type DailyLogAutodeskTemplate,
  type DailyLogWalkRow,
  type ItemEditDirectoryOption,
  type MutationResult,
  type WalkPhotoResponse,
} from "../api";
import { EvidenceTile } from "../components/chrome";
import { useResource } from "../useResource";
import { ADR, C, FONT_MONO } from "../tokens";
import { autodeskReportSections, savedAutodeskDisplayValue } from "./AutodeskReportSections";

const INK = C.ink;
const SUB = C.sub;
const LABEL = ADR.label;
const FAINT = C.sub2;
const CARD_LINE = "rgba(20,17,12,.08)";
const ROW_LINE = "rgba(20,17,12,.05)";
const HOVER = ADR.hover;
const RECESS = ADR.recess;
const MENU_ON = ADR.menuOn;

const mono: CSSProperties = { fontFamily: FONT_MONO };
const eyebrow: CSSProperties = { ...mono, fontSize: 9, fontWeight: 700, letterSpacing: "1.3px", color: LABEL, textTransform: "uppercase" };
const card: CSSProperties = { border: `1px solid ${CARD_LINE}`, borderRadius: 14, background: ADR.card, overflow: "hidden", scrollMarginTop: 12 };
const cardHead: CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "13px 18px", borderBottom: "1px solid rgba(20,17,12,.06)" };
const h2: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: "-.25px", flex: 1 };
const metaText: CSSProperties = { ...mono, fontSize: 9.5, fontWeight: 600, letterSpacing: ".6px", color: FAINT };

/** The stylesheet owns every state an inline style would outrank (hover,
 *  focus, the empty placeholder, the reveal-on-hover delete). */
const STYLES = `
.adr-cell{width:100%;height:36px;border:0;outline:none;background:transparent;border-radius:6px;padding:0 8px;font:inherit;font-size:13.5px;color:${INK};transition:background .1s}
.adr-cell:hover:not(:disabled){background:${HOVER}}
.adr-cell:focus{background:${ADR.paper};box-shadow:0 0 0 2px rgba(20,17,12,.14)}
.adr-cell.num{text-align:right;font-variant-numeric:tabular-nums}
.adr-cell::placeholder{color:${FAINT};opacity:1}
.adr-cell:disabled{color:${INK};cursor:default}
.adr-trow .adr-del{opacity:0;transition:opacity .12s}
.adr-trow:hover .adr-del,.adr-trow:focus-within .adr-del{opacity:1}
.adr-del:hover{background:${ADR.redSoft} !important;color:${ADR.redInk} !important}
.adr-ph .adr-rm{opacity:0;transition:opacity .12s}
.adr-ph:hover .adr-rm,.adr-ph:focus-within .adr-rm{opacity:1}
.adr-rm:hover{background:${ADR.red} !important}
.adr-ed{outline:none;border-radius:6px;cursor:text}
.adr-ed[contenteditable=true]:hover{background:${HOVER}}
.adr-ed:focus{background:${ADR.paper};box-shadow:0 0 0 2px rgba(20,17,12,.14)}
.adr-ed:empty::before{content:attr(data-placeholder);color:${FAINT}}
.adr-hov:hover{background:${HOVER} !important}
.adr-menu-item:hover{background:${MENU_ON} !important}
.adr-nav:hover{background:${MENU_ON} !important}
.adr-add:hover{background:${RECESS} !important;color:${INK} !important}
.adr-link:hover{text-decoration:underline;text-underline-offset:3px}
.adr-from:hover{color:${INK} !important}
@keyframes adr-pulse{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes adr-fade{from{opacity:0}to{opacity:1}}
@media (prefers-reduced-motion: reduce){.adr-pulse{animation:none !important}}
.adr-tablewrap{overflow-x:auto}
.adr-tableinner{min-width:720px}
.adr-weather{flex-wrap:wrap}
.adr-weather>div{min-width:190px}
@media (max-width: 1180px){
  .adr-grid{grid-template-columns:minmax(0,1fr) !important}
  .adr-sections{display:none !important}
}
@media (max-width: 760px){
  .adr-head{flex-direction:column}
  .adr-head>div:last-child{align-items:flex-start !important}
  .adr-basic{grid-template-columns:minmax(0,1fr) !important}
}
`;

/** The report's hover/focus rules, for the review card's Autodesk pieces. */
export function AutodeskStyles() {
  return <style>{STYLES}</style>;
}

export interface AutodeskDailyReportProps {
  template: DailyLogAutodeskTemplate;
  /** The walks that fed this day's Form, oldest first. */
  walks: DailyLogWalkRow[];
  /** Every record of those walks, any review state. */
  rows: ConsoleRow[];
  jobSiteId: string;
  search: string;
  refreshKey: number;
  /** Ask the page for fresh data (an edit landed, or a sync is running). */
  onChanged: () => void;
  onSelectEntry: (id: string) => void;
  hrefForWalk: (visitId: string) => string;
  onNavigate: (href: string) => void;
  /** Where the day's records still waiting in review are, if any. */
  review?: { count: number; href: string };
}

type SaveState = "saving" | "savefail" | "syncing" | "syncfail" | "synced" | "readonly";
/** How long an edit may wait for a publish that carries it. */
const SYNC_GIVE_UP_MS = 180_000;

export function AutodeskDailyReport(props: AutodeskDailyReportProps) {
  const { template, walks, rows, search, refreshKey, onChanged } = props;
  const plan = template.plan;
  const editable = template.editable !== false;
  const primaryVisitId = walks[walks.length - 1]?.visitId ?? walks[0]?.visitId ?? "";
  const overrides = template.overrides ?? {};
  const accepted = useMemo(() => rows.filter((row) => row.reviewStatus === "accepted"), [rows]);

  const savedResource = useResource<{ report: AutodeskSavedForm }>(
    template.formId ? `autodesk-report/${primaryVisitId}/${template.formId}/${template.syncedAt ?? ""}${search}` : null,
    () => fetchAutodeskReport(primaryVisitId, search),
    refreshKey,
  );
  const saved = savedResource.data?.report?.layoutId === plan.layoutId ? savedResource.data.report : undefined;

  // ---- save + sync state --------------------------------------------------
  const [inFlight, setInFlight] = useState(0);
  const [saveError, setSaveError] = useState<{ message: string; retry: () => void } | null>(null);
  /**
   * When the last edit was saved, on the SERVER's clock (the publisher stamps
   * `attemptedAt` on the same clock). The edit is in Autodesk once a publish
   * that STARTED at or after it has finished: one already under way when the
   * edit landed read the record before it, and its "synced" says nothing
   * about this edit (LED-792: two quick edits read Synced, ACC had neither).
   */
  const [syncSince, setSyncSince] = useState<number | null>(null);
  /** The same moment on this browser's clock, for the give-up timer. */
  const [syncSinceLocal, setSyncSinceLocal] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [dirty, setDirty] = useState<ReadonlySet<string>>(new Set());
  const [freshRows, setFreshRows] = useState<ReadonlySet<string>>(new Set());
  const syncedAt = template.syncedAt ? Date.parse(template.syncedAt) : 0;
  const attemptedAt = template.attemptedAt ? Date.parse(template.attemptedAt) : 0;
  const pending = template.syncStatus === "pending";
  const caughtUp = syncSince !== null && !pending && template.syncStatus === "synced" && attemptedAt >= syncSince && syncedAt >= attemptedAt;
  const failedSinceEdit = syncSince !== null && !pending && template.syncStatus === "failed" && attemptedAt >= syncSince;
  useEffect(() => {
    if (caughtUp || failedSinceEdit) {
      setSyncSince(null);
      setSyncSinceLocal(null);
      if (caughtUp) { setDirty(new Set()); setFreshRows(new Set()); }
    }
  }, [caughtUp, failedSinceEdit]);
  // Three minutes with no publish that carries the edit: say so, with Retry,
  // rather than read "Syncing" for good.
  const unconfirmed = syncSinceLocal !== null && !caughtUp && !failedSinceEdit && now - syncSinceLocal > SYNC_GIVE_UP_MS;
  const syncing = !unconfirmed && (pending || (syncSince !== null && !caughtUp && !failedSinceEdit));
  const state: SaveState = !editable
    ? "readonly"
    : inFlight > 0
      ? "saving"
      : saveError
        ? "savefail"
        : syncing
          ? "syncing"
          : unconfirmed || template.syncStatus === "failed"
            ? "syncfail"
            : "synced";

  // While Autodesk catches up, re-read the day; after three minutes the
  // badge gives up (above) and the re-reads stop with it.
  useEffect(() => {
    if (!syncing) return;
    const timer = setTimeout(() => { setNow(Date.now()); onChanged(); }, 2_000);
    return () => clearTimeout(timer);
  }, [syncing, refreshKey, onChanged, now]);

  const save = async (keys: string[], run: () => Promise<MutationResult>, retry?: () => void): Promise<boolean> => {
    setInFlight((count) => count + 1);
    setDirty((current) => new Set([...current, ...keys]));
    const result = await run().catch((error: unknown) => ({ ok: false, status: 0, error: error instanceof Error ? error.message : String(error) }) as MutationResult);
    setInFlight((count) => count - 1);
    if (!result.ok) {
      setSaveError({ message: result.error || "The report could not be saved.", retry: retry ?? (() => setSaveError(null)) });
      return false;
    }
    setSaveError(null);
    // Without the server's clock, allow for a little skew.
    setSyncSince(result.serverAt ?? Date.now() - 1_500);
    setSyncSinceLocal(Date.now());
    setNow(Date.now());
    onChanged();
    return true;
  };
  const override = (keys: string[], patch: AutodeskReportOverridePatch) => {
    const run = () => postAutodeskReportOverride(primaryVisitId, search, patch);
    return save(keys, run, () => void save(keys, run));
  };
  const editRecord = (row: ConsoleRow, patch: Record<string, unknown>) => {
    const run = () => postItemEdit("log", row.id, search, patch);
    return save([`row:${row.id}`], run, () => void save([`row:${row.id}`], run));
  };

  const markFor = (key: string, inAutodesk: boolean) =>
    dirty.has(key) && state !== "synced" && state !== "syncfail"
      ? "syncing" as const
      : inAutodesk ? "synced" as const : "absent" as const;

  // ---- directories (locations, companies) ----------------------------------
  const [locations, setLocations] = useState<ItemEditDirectoryOption[] | null>(null);
  useEffect(() => {
    let live = true;
    fetchItemEditOptions(props.jobSiteId, undefined, search, primaryVisitId)
      .then((options) => { if (live) setLocations(options.locations); })
      .catch(() => { if (live) setLocations([]); });
    return () => { live = false; };
  }, [props.jobSiteId, primaryVisitId, search]);

  // ---- sections ------------------------------------------------------------
  const presentation = useMemo(() => autodeskReportSections(plan, accepted), [plan, accepted]);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const bind = (id: string) => (element: HTMLElement | null) => {
    if (element) sectionRefs.current.set(id, element);
    else sectionRefs.current.delete(id);
  };
  const jump = (id: string) => sectionRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const tableSectionIds = new Set((plan.tableBindings ?? []).map((binding) => binding.sectionId));
  const answers = new Map((overrides.answers ?? []).map((answer) => [answer.fieldId, answer.value]));
  const questionGroups = (sectionId: string) =>
    presentation.sections.find((section) => section.id === sectionId)?.groups.filter((group) => group.kind === "question") ?? [];

  const answerFor = (group: AutodeskDailyLogDisplayGroup & { rows: ConsoleRow[] }): string[] => {
    const typed = group.fieldId ? answers.get(group.fieldId) : undefined;
    if (typed !== undefined) return Array.isArray(typed) ? typed : [typed];
    const savedValue = saved?.questions.find((value) => value.fieldId === group.fieldId);
    const fromSaved = savedValue ? savedAnswer(savedValue) : null;
    if (fromSaved) return fromSaved;
    if (group.valueKind === "toggle") return [group.rows.length > 0 ? "Yes" : "No"];
    return [];
  };

  const sortedSections = [...plan.sections].sort((a, b) => a.displayIndex - b.displayIndex);
  type Block = { id: string; label: string; meta: string; node: ReactNode };
  const blocks: Block[] = [];
  const pushWeather = () =>
    blocks.push({ id: "weather", label: "Weather", meta: "auto", node: <WeatherCard key="weather" refEl={bind("weather")} saved={saved} loading={savedResource.loading && !saved} formDate={template.formDate} /> });
  let weatherPlaced = false;
  for (const section of sortedSections) {
    if (section.kind === "basic_information") {
      blocks.push({
        id: section.id,
        label: "Basic information",
        meta: "",
        node: (
          <BasicInformation
            key={section.id}
            refEl={bind(section.id)}
            editable={editable}
            description={overrides.description ?? saved?.description ?? ""}
            formDate={template.formDate ?? saved?.formDate}
            locationId={overrides.locationId !== undefined ? overrides.locationId : saved?.locationId}
            locations={locations}
            walks={walks}
            attachmentMediaIds={template.attachmentMediaIds ?? []}
            excludedMediaIds={overrides.excludedMediaIds ?? []}
            jobSiteId={props.jobSiteId}
            search={search}
            refreshKey={refreshKey}
            onDescription={(text) => override(["description"], { description: text })}
            onLocation={(locationId) => override(["location"], { locationId })}
            onPhoto={(mediaId, excluded) => override(["photos"], { photo: { mediaId, excluded } })}
          />
        ),
      });
      pushWeather();
      weatherPlaced = true;
      continue;
    }
    if (!weatherPlaced) { pushWeather(); weatherPlaced = true; }
    if (tableSectionIds.has(section.id)) {
      const bindings = (plan.tableBindings ?? []).filter((binding) => binding.sectionId === section.id);
      const categories = new Set(bindings.map((binding) => binding.sourceCategory));
      const tableRows = accepted
        .filter((row) => categories.has(row.category as ProcoreDailyLogCategory))
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
      blocks.push({
        id: section.id,
        label: section.label,
        meta: String(tableRows.length),
        node: (
          <TableCard
            key={section.id}
            refEl={bind(section.id)}
            section={section}
            category={bindings[0]!.sourceCategory}
            rows={tableRows}
            cells={template.cells ?? {}}
            editable={editable}
            syncedIds={new Set(template.syncedItemIds ?? [])}
            fresh={freshRows}
            markFor={markFor}
            jobSiteId={props.jobSiteId}
            visitId={primaryVisitId}
            search={search}
            onEdit={editRecord}
            onRemove={(row) => {
              const run = () => postReviewDecision("log", row.id, search, "rejected");
              return save([`row:${row.id}`], run, () => void save([`row:${row.id}`], run));
            }}
            onAdd={async (fieldValues, title, company) => {
              const run = () => postAutodeskReportRow(primaryVisitId, search, {
                category: bindings[0]!.sourceCategory, fieldValues, ...(title ? { title } : {}),
                ...(company ? { selectedCompanyRemoteId: company.remoteId, selectedCompanyName: company.name } : {}),
              });
              let created: string | undefined;
              const ok = await save([`table:${section.id}`], async () => {
                const result = await run();
                created = (result.result as { itemId?: string } | undefined)?.itemId;
                return result;
              });
              if (ok && created) setFreshRows((current) => new Set([...current, created!]));
              return ok;
            }}
          />
        ),
      });
      continue;
    }
    if (section.kind === "notes") {
      blocks.push({
        id: section.id,
        label: section.label,
        meta: "",
        node: (
          <TextCard
            key={section.id}
            refEl={bind(section.id)}
            title={section.label}
            text={overrides.notes ?? saved?.notes ?? ""}
            editable={editable}
            placeholder="Add notes for this report"
            onCommit={(text) => override(["notes"], { notes: text })}
          />
        ),
      });
      continue;
    }
    const groups = presentation.sections.find((candidate) => candidate.id === section.id)?.groups ?? [];
    const questions = questionGroups(section.id);
    const manual = groups.filter((group) => group.kind === "manual");
    if (questions.length > 0) {
      const yes = questions.filter((group) => isAffirmative(group, answerFor(group))).length;
      blocks.push({
        id: section.id,
        label: section.label,
        meta: `${yes}/${questions.length}`,
        node: (
          <EventsCard
            key={section.id}
            refEl={bind(section.id)}
            label={section.label}
            groups={questions}
            manual={manual}
            saved={saved}
            answerFor={answerFor}
            editable={editable}
            markFor={markFor}
            inAutodesk={!!template.formId}
            jobSiteId={props.jobSiteId}
            onAnswer={(group, value) => override([`q:${group.fieldId}`], { answer: { fieldId: group.fieldId!, value } })}
            onSelectEntry={props.onSelectEntry}
          />
        ),
      });
      continue;
    }
    if (manual.length > 0 || section.kind === "signature") {
      blocks.push({
        id: section.id,
        label: section.kind === "signature" ? "Signature" : section.label,
        meta: "",
        node: <ManualCard key={section.id} refEl={bind(section.id)} section={section} groups={manual} saved={saved} formUrl={formUrl(template)} />,
      });
    }
  }
  if (!weatherPlaced) pushWeather();

  const title = overrides.name ?? saved?.name ?? template.formName ?? `Daily report — ${template.formDate ?? ""}`;
  const url = formUrl(template);
  const firstWalk = walks[0];
  const walkerName = firstWalk?.walkerName;
  const walkTime = firstWalk?.startedAt ? shortTime(firstWalk.startedAt) : null;
  const minutes = walks.reduce((sum, walk) => sum + (walk.recordedMinutes ?? 0), 0);

  return (
    <div data-testid="autodesk-daily-report" style={{ animation: "adr-fade .18s both" }}>
      <style>{STYLES}</style>
      <div className="adr-head" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginTop: 22 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <EditableText
            as="h1"
            testId="autodesk-report-title"
            text={title}
            editable={editable}
            singleLine
            placeholder="Report name"
            onCommit={(text) => text.trim() ? override(["name"], { name: text.trim() }) : undefined}
            style={{ margin: "0 0 0 -6px", padding: "2px 6px", fontSize: 26, fontWeight: 800, letterSpacing: "-.8px", lineHeight: 1.15 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 13, color: SUB, flexWrap: "wrap" }}>
            {walks.map((walk, index) => (
              <span key={walk.visitId} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {index > 0 && <Dot />}
                {index === 0 && <Footprints size={13} color={SUB} aria-hidden />}
                <a
                  href={props.hrefForWalk(walk.visitId)}
                  className="adr-link"
                  onClick={(event) => { event.preventDefault(); props.onNavigate(props.hrefForWalk(walk.visitId)); }}
                  style={{ fontWeight: 600, color: INK, textDecoration: "none" }}
                >
                  {walk.name || "Untitled walk"}
                </a>
              </span>
            ))}
            {walkerName && <><Dot /><span>{walkerName}</span></>}
            {walkTime && <><Dot /><span>{walkTime}{minutes > 0 ? ` · ${minutes} min` : ""}</span></>}
          </div>
        </div>
        <div style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <SaveBadge
            state={state}
            syncedAt={template.syncedAt}
            error={saveError?.message ?? (unconfirmed ? "Autodesk has not confirmed the last edit. Retry to publish the report again." : template.syncError)}
            onRetry={() => saveError ? saveError.retry() : void override(["sync"], {})}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {props.review && props.review.count > 0 && (
              <a
                href={props.review.href}
                onClick={(event) => { event.preventDefault(); props.onNavigate(props.review!.href); }}
                className="adr-link"
                data-testid="autodesk-report-review-link"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: ADR.amberInk, textDecoration: "none" }}
              >
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: C.amber }} />
                {props.review.count} in review
              </a>
            )}
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="adr-link" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600, color: INK, paddingRight: 4, textDecoration: "none" }}>
                View in Autodesk <ExternalLink size={13} color={SUB} aria-hidden />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="adr-grid" style={{ display: "grid", gridTemplateColumns: "172px minmax(0,1fr)", gap: 22, marginTop: 18, alignItems: "start" }}>
        <nav aria-label="Report sections" className="adr-sections" style={{ position: "sticky", top: 12, display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ ...eyebrow, color: FAINT, padding: "0 10px 8px" }}>Sections</div>
          {blocks.map((block) => (
            <button
              key={block.id}
              type="button"
              className="adr-nav"
              onClick={() => jump(block.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, height: 34, padding: "0 10px", borderRadius: 8, border: 0, background: "transparent", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 500, color: INK, font: "inherit" }}
            >
              <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 13 }}>{block.label}</span>
              <span style={{ ...mono, fontSize: 9.5, fontWeight: 600, color: FAINT }}>{block.meta}</span>
            </button>
          ))}
        </nav>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {blocks.map((block) => block.node)}
          {presentation.unmatched.length > 0 && (
            <section data-testid="autodesk-unmapped-entries" style={card}>
              <div style={cardHead}><h2 style={h2}>Additional entries</h2><span style={metaText}>{presentation.unmatched.length}</span></div>
              {presentation.unmatched.map((row) => (
                <EntryLink key={row.id} row={row} onSelect={props.onSelectEntry} style={{ padding: "10px 18px", borderTop: `1px solid ${ROW_LINE}` }} />
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header pieces
// ---------------------------------------------------------------------------

function Dot() {
  return <span aria-hidden style={{ color: ADR.dot }}>·</span>;
}

/** Autodesk's folded-ribbon A — the same path the sign-in panel draws. */
export function AutodeskGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block", flex: "none" }}>
      <path d="m.129 20.202 14.7-9.136h7.625c.235 0 .445.188.445.445 0 .21-.092.305-.21.375l-7.222 4.323c-.47.283-.633.845-.633 1.265l-.008 2.725H24V4.362a.561.561 0 0 0-.585-.562h-8.752L0 12.893V20.2h.129z" fill={INK} />
    </svg>
  );
}

function SaveBadge({ state, syncedAt, error, onRetry }: { state: SaveState; syncedAt?: string; error?: string; onRetry: () => void }) {
  if (state === "readonly") return null;
  const looks: Record<Exclude<SaveState, "readonly">, { label: string; icon: ReactNode; fg: string; bg: string; pulse: boolean; title: string }> = {
    saving: { label: "Saving", icon: <Loader size={14} color={SUB} />, fg: SUB, bg: MENU_ON, pulse: true, title: "Saving your edit" },
    syncing: { label: "Syncing", icon: <RefreshCw size={14} color={ADR.blueInk} />, fg: ADR.blueInk, bg: ADR.blueSoft, pulse: true, title: "Syncing to Autodesk" },
    synced: { label: "Synced", icon: <AutodeskGlyph size={15} />, fg: ADR.green, bg: ADR.greenSoft, pulse: false, title: syncedAt ? `Synced to Autodesk ${shortTime(syncedAt)}` : "Synced to Autodesk" },
    savefail: { label: "Not saved · Retry", icon: <CircleAlert size={14} color={ADR.redInk} />, fg: ADR.redInk, bg: ADR.redSoft, pulse: false, title: error ?? "The edit was not saved" },
    syncfail: { label: "Sync failed · Retry", icon: <CircleAlert size={14} color={ADR.redInk} />, fg: ADR.redInk, bg: ADR.redSoft, pulse: false, title: error ?? "Autodesk did not accept the last sync" },
  };
  const look = looks[state];
  const failed = state === "savefail" || state === "syncfail";
  const body = (
    <>
      <span className={look.pulse ? "adr-pulse" : undefined} style={{ display: "inline-flex", animation: look.pulse ? "adr-pulse 1.1s ease-in-out infinite" : undefined }}>{look.icon}</span>
      <span style={{ ...mono, fontSize: 10.5, fontWeight: 700, letterSpacing: "1.6px", textTransform: "uppercase" }}>{look.label}</span>
    </>
  );
  const style: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 14px 0 11px", borderRadius: 999, background: look.bg, color: look.fg, border: 0, font: "inherit" };
  return failed ? (
    <button type="button" data-testid="autodesk-report-status" data-state={state} title={look.title} onClick={onRetry} style={{ ...style, cursor: "pointer" }}>{body}</button>
  ) : (
    <span role="status" data-testid="autodesk-report-status" data-state={state} title={look.title} style={style}>{body}</span>
  );
}

function SyncMark({ mark }: { mark: "syncing" | "synced" | "absent" }) {
  if (mark === "syncing") {
    return <span title="Syncing to Autodesk" className="adr-pulse" style={{ display: "inline-flex", animation: "adr-pulse 1.2s ease-in-out infinite" }}><RefreshCw size={12} color={ADR.blueInk} /></span>;
  }
  if (mark === "synced") return <span title="Synced to Autodesk" style={{ display: "inline-flex" }}><Check size={12} color={ADR.greenMark} strokeWidth={2.6} /></span>;
  return <span title="Not in Autodesk yet" style={{ display: "inline-flex" }}><Circle size={12} color={ADR.dot} /></span>;
}

// ---------------------------------------------------------------------------
// Editable text
// ---------------------------------------------------------------------------

/**
 * Text edited in place. Commits on blur (and Enter, for a single line), and
 * only when the text changed. While focused, a re-render with fresh server
 * data never overwrites what the reader is typing.
 */
function EditableText({ as: Tag = "div", text, editable, onCommit, style, placeholder, singleLine = false, testId }: {
  as?: "div" | "h1";
  text: string;
  editable: boolean;
  onCommit: (text: string) => unknown;
  style?: CSSProperties;
  placeholder?: string;
  singleLine?: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const focused = useRef(false);
  const committed = useRef(text);
  const initial = useRef(text);
  // Seeded once, on mount. An inline ref ran again on every render and wrote
  // the saved text into a box the reader had just emptied to retype.
  const attach = useCallback((element: HTMLElement | null) => {
    ref.current = element;
    if (element && !element.innerText && initial.current) element.innerText = initial.current;
  }, []);
  useEffect(() => {
    if (!focused.current && ref.current && ref.current.innerText !== text) ref.current.innerText = text;
    if (!focused.current) committed.current = text;
  }, [text]);
  const commit = () => {
    const next = (ref.current?.innerText ?? "").replace(/ /g, " ").trimEnd();
    if (next !== committed.current) {
      committed.current = next;
      onCommit(next);
    }
  };
  return (
    <Tag
      ref={attach}
      className="adr-ed"
      data-testid={testId}
      data-placeholder={placeholder}
      role={editable ? "textbox" : undefined}
      aria-label={placeholder}
      aria-multiline={editable && !singleLine ? true : undefined}
      contentEditable={editable}
      suppressContentEditableWarning
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; commit(); }}
      onKeyDown={(event) => {
        if (event.key === "Escape") { if (ref.current) ref.current.innerText = committed.current; (event.target as HTMLElement).blur(); }
        if (singleLine && event.key === "Enter") { event.preventDefault(); (event.target as HTMLElement).blur(); }
      }}
      style={style}
    />
  );
}

// ---------------------------------------------------------------------------
// Basic information
// ---------------------------------------------------------------------------

function BasicInformation(props: {
  refEl: (element: HTMLElement | null) => void;
  editable: boolean;
  description: string;
  formDate?: string;
  locationId?: string | null;
  locations: ItemEditDirectoryOption[] | null;
  walks: DailyLogWalkRow[];
  attachmentMediaIds: string[];
  excludedMediaIds: string[];
  jobSiteId: string;
  search: string;
  refreshKey: number;
  onDescription: (text: string) => unknown;
  onLocation: (locationId: string | null) => unknown;
  onPhoto: (mediaId: string, excluded: boolean) => unknown;
}) {
  const [locOpen, setLocOpen] = useState(false);
  const location = props.locationId ? props.locations?.find((option) => option.remoteId === props.locationId) : undefined;
  const locationLabel = location ? splitPath(location) : props.locationId ? { parent: "", name: props.locations === null ? "…" : "Location set in Autodesk" } : null;
  return (
    <section ref={props.refEl} data-autodesk-section="basic_information" style={card}>
      <div style={cardHead}><h2 style={h2}>Basic information</h2></div>
      <div style={{ padding: "14px 18px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ ...eyebrow, marginBottom: 6 }}>Description</div>
          <EditableText
            testId="autodesk-report-description"
            text={props.description}
            editable={props.editable}
            placeholder="Describe the day"
            onCommit={props.onDescription}
            style={{ margin: "0 -6px", padding: "4px 6px", fontSize: 14, lineHeight: 1.55, textWrap: "pretty", minHeight: 24 } as CSSProperties}
          />
        </div>
        <div className="adr-basic" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <div style={{ ...eyebrow, marginBottom: 6 }}>Date</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, fontSize: 13.5, color: INK }}>
              <Calendar size={14} color={SUB} aria-hidden />
              {props.formDate ? longDate(props.formDate) : "—"}
            </div>
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ ...eyebrow, marginBottom: 6 }}>Location</div>
            <button
              type="button"
              className={props.editable ? "adr-hov" : undefined}
              disabled={!props.editable}
              aria-haspopup="listbox"
              aria-expanded={locOpen}
              data-testid="autodesk-report-location"
              onClick={() => setLocOpen((open) => !open)}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 10px 0 8px", marginLeft: -8, borderRadius: 7, border: 0, background: locOpen ? HOVER : "transparent", fontSize: 13.5, color: INK, cursor: props.editable ? "pointer" : "default", font: "inherit", maxWidth: "100%" }}
            >
              <MapPin size={14} color={SUB} aria-hidden />
              {locationLabel ? (
                <span style={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {locationLabel.parent && <span style={{ color: LABEL }}>{locationLabel.parent} › </span>}{locationLabel.name}
                </span>
              ) : (
                <span style={{ fontSize: 13.5, color: FAINT }}>No location</span>
              )}
              {props.editable && <ChevronDown size={13} color={SUB} aria-hidden />}
            </button>
            {locOpen && (
              <Menu onClose={() => setLocOpen(false)} style={{ left: -8, top: 66, width: 280 }}>
                {(props.locations ?? []).length === 0 && <div style={{ padding: "8px 10px", fontSize: 13, color: FAINT }}>{props.locations === null ? "Loading locations…" : "This project has no locations"}</div>}
                {props.locationId && (
                  <MenuItem onPick={() => { setLocOpen(false); props.onLocation(null); }} label={<span style={{ color: SUB }}>No location</span>} />
                )}
                {(props.locations ?? []).map((option) => {
                  const parts = splitPath(option);
                  return (
                    <MenuItem
                      key={option.remoteId}
                      on={option.remoteId === props.locationId}
                      onPick={() => { setLocOpen(false); if (option.remoteId !== props.locationId) props.onLocation(option.remoteId); }}
                      label={<span>{parts.parent && <span style={{ color: LABEL }}>{parts.parent} › </span>}{parts.name}</span>}
                    />
                  );
                })}
              </Menu>
            )}
          </div>
        </div>
        <ReportPhotos
          walks={props.walks}
          attached={props.attachmentMediaIds}
          excluded={props.excludedMediaIds}
          editable={props.editable}
          jobSiteId={props.jobSiteId}
          search={props.search}
          refreshKey={props.refreshKey}
          onPhoto={props.onPhoto}
        />
      </div>
    </section>
  );
}

function ReportPhotos(props: {
  walks: DailyLogWalkRow[];
  attached: string[];
  excluded: string[];
  editable: boolean;
  jobSiteId: string;
  search: string;
  refreshKey: number;
  onPhoto: (mediaId: string, excluded: boolean) => unknown;
}) {
  const visitIds = props.walks.map((walk) => walk.visitId);
  const resource = useResource<WalkPhotoResponse[]>(
    `autodesk-report-photos/${visitIds.join(",")}${props.search}`,
    async () => (await Promise.all(visitIds.map((visitId) => fetchWalkDetail(visitId, props.search).then((detail) => detail.photos).catch(() => [] as WalkPhotoResponse[])))).flat(),
    props.refreshKey,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  const excluded = new Set(props.excluded);
  const attached = new Set(props.attached);
  const images = (resource.data ?? []).filter((photo) => photo.type === "image");
  const onForm = images.filter((photo) => (attached.has(photo.id) && !excluded.has(photo.id) && !hidden.has(photo.id)) || shown.has(photo.id));
  const addable = images.filter((photo) => excluded.has(photo.id) && !shown.has(photo.id));
  return (
    <div>
      <div style={{ ...eyebrow, marginBottom: 8 }}>Photos <span style={{ color: ADR.faintRule }}>· {onForm.length}</span></div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", position: "relative" }}>
        {onForm.map((photo) => (
          <div key={photo.id} className="adr-ph" style={{ position: "relative", width: 112, height: 84, borderRadius: 9, overflow: "hidden", background: ADR.tile }}>
            <EvidenceTile jobSiteId={props.jobSiteId} url={photo.thumbnailUrl ?? photo.url} height={84} alt={photo.description ?? ""} />
            <span style={{ ...mono, position: "absolute", left: 6, bottom: 6, fontSize: 8, fontWeight: 700, letterSpacing: ".7px", color: ADR.card, background: "rgba(10,10,10,.6)", padding: "3px 5px", borderRadius: 4 }}>{shortTime(photo.capturedAt).toUpperCase()}</span>
            {props.editable && (
              <button
                type="button"
                className="adr-rm"
                aria-label="Remove from the report"
                title="Remove from the report"
                onClick={() => {
                  setHidden((current) => new Set([...current, photo.id]));
                  setShown((current) => { const next = new Set(current); next.delete(photo.id); return next; });
                  props.onPhoto(photo.id, true);
                }}
                style={{ position: "absolute", right: 6, top: 6, width: 24, height: 24, borderRadius: 12, border: 0, background: "rgba(10,10,10,.6)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: ADR.card }}
              >
                <MinusCircle size={16} aria-hidden />
              </button>
            )}
          </div>
        ))}
        {props.editable && addable.length > 0 && (
          <button
            type="button"
            aria-label="Add photo"
            aria-haspopup="listbox"
            aria-expanded={addOpen}
            className="adr-hov"
            onClick={() => setAddOpen((open) => !open)}
            style={{ width: 112, height: 84, borderRadius: 9, border: "1.5px dashed rgba(20,17,12,.16)", background: "transparent", display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer", color: LABEL }}
          >
            <Plus size={14} strokeWidth={2.2} />
            <span style={{ ...mono, fontSize: 8, fontWeight: 700, letterSpacing: ".9px" }}>ADD</span>
          </button>
        )}
        {onForm.length === 0 && addable.length === 0 && (
          <span style={{ fontSize: 13, color: FAINT }}>{resource.loading && !resource.data ? "Loading photos…" : "No photos on this report"}</span>
        )}
        {addOpen && (
          <Menu onClose={() => setAddOpen(false)} style={{ left: 0, top: 92, width: 360, padding: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {addable.map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  aria-label="Add this photo"
                  onClick={() => {
                    setShown((current) => new Set([...current, photo.id]));
                    setHidden((current) => { const next = new Set(current); next.delete(photo.id); return next; });
                    setAddOpen(false);
                    props.onPhoto(photo.id, false);
                  }}
                  style={{ width: 80, height: 60, padding: 0, border: 0, borderRadius: 7, overflow: "hidden", cursor: "pointer", background: ADR.tile }}
                >
                  <EvidenceTile jobSiteId={props.jobSiteId} url={photo.thumbnailUrl ?? photo.url} height={60} alt={photo.description ?? ""} />
                </button>
              ))}
            </div>
          </Menu>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weather (Autodesk fills it)
// ---------------------------------------------------------------------------

function WeatherCard({ refEl, saved, loading, formDate }: { refEl: (element: HTMLElement | null) => void; saved?: AutodeskSavedForm; loading: boolean; formDate?: string }) {
  const hours = pickWeatherHours(saved?.weather?.hours ?? []);
  return (
    <section ref={refEl} data-autodesk-section="weather" style={card}>
      <div style={cardHead}>
        <h2 style={h2}>Weather</h2>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: SUB }}>
          <CloudDownload size={13} color={SUB} aria-hidden />
          Filled by Autodesk{formDate ? ` for ${monthDay(formDate)}` : ""}
        </span>
      </div>
      {hours.length === 0 ? (
        <div style={{ padding: "14px 18px", fontSize: 13, color: FAINT }}>
          {loading ? "Loading weather…" : saved ? "No weather recorded" : "Weather unavailable"}
        </div>
      ) : (
        <div className="adr-weather" style={{ display: "flex", alignItems: "center", padding: "4px 10px" }}>
          {hours.map((hour, index) => {
            const Icon = weatherIcon(hour.summary);
            return (
              <div key={hour.hour} style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12, padding: "12px 8px", borderRight: index < hours.length - 1 ? "1px solid rgba(20,17,12,.10)" : "none" }}>
                <Icon size={18} color={SUB} aria-hidden />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {hour.temperatureC !== undefined ? `${Math.round(hour.temperatureC * 9 / 5 + 32)}°F ` : ""}<span style={{ fontWeight: 500, color: SUB }}>{hour.summary}</span>
                  </div>
                  <div style={{ ...metaText, marginTop: 2 }}>
                    {hourLabel(hour.hour)}
                    {hour.windKph !== undefined ? ` · ${Math.round(hour.windKph / 1.609)} mph` : ""}
                    {hour.humidity !== undefined ? ` · ${Math.round(hour.humidity <= 1 ? hour.humidity * 100 : hour.humidity)}%` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tables (Manpower, Deliveries, Equipment, custom)
// ---------------------------------------------------------------------------

const NUMERIC_COLUMNS = new Set(["integer_val", "number_val", "timespan_val"]);

function TableCard(props: {
  refEl: (element: HTMLElement | null) => void;
  section: AutodeskTemplateSectionSnapshot;
  category: ProcoreDailyLogCategory;
  rows: ConsoleRow[];
  cells: NonNullable<DailyLogAutodeskTemplate["cells"]>;
  editable: boolean;
  syncedIds: ReadonlySet<string>;
  fresh: ReadonlySet<string>;
  markFor: (key: string, inAutodesk: boolean) => "syncing" | "synced" | "absent";
  jobSiteId: string;
  visitId: string;
  search: string;
  onEdit: (row: ConsoleRow, patch: Record<string, unknown>) => Promise<boolean>;
  onRemove: (row: ConsoleRow) => Promise<boolean>;
  onAdd: (fieldValues: Record<string, string | number | null>, title?: string, company?: ItemEditDirectoryOption) => Promise<boolean>;
}) {
  // Manpower reads as company, crew size, hours and work: Discipline and Role
  // stay on the Autodesk form but are not shown or edited here.
  const manpower = props.category === "manpower" || props.category === "work_logs";
  const columns = props.section.columns.filter((column) =>
    column.columnType !== "calculated" &&
    column.columnType !== "signature_val" &&
    !(manpower && /discipline|trade|role/i.test(`${column.label} ${column.columnKey ?? ""}`)));
  // Numbers get a column their header fits in; the long text column (work
  // performed, comment) gets the most room, as the design draws it.
  const gridColumns = `20px ${columns.map((column) => {
    if (NUMERIC_COLUMNS.has(column.columnType ?? "")) return `${Math.max(76, Math.ceil(column.label.length * 6.7 + 20))}px`;
    if (/work performed|comment|description|note/i.test(column.label)) return "minmax(0,2.2fr)";
    if (column.columnType === "uid_val" && /role/i.test(column.label)) return "minmax(0,1fr)";
    if (/discipline|trade/i.test(column.label)) return "minmax(0,1.35fr)";
    if (/unit/i.test(column.label)) return "minmax(0,1fr)";
    return "minmax(0,2.3fr)";
  }).join(" ")} 32px`;
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());
  const [companies, setCompanies] = useState<ItemEditDirectoryOption[] | null>(null);
  const [companiesState, setCompaniesState] = useState<CompanyDirectoryState>("idle");
  const hasCompanyColumn = columns.some((column) => column.columnType === "uid_val" && /company/i.test(`${column.label} ${column.columnKey ?? ""}`));
  const loadCompanies = () => {
    if (!hasCompanyColumn || companiesState === "loading" || companiesState === "ready") return;
    setCompaniesState("loading");
    fetchItemEditOptions(props.jobSiteId, props.category, props.search, props.visitId)
      .then((options) => { setCompanies(options.companies); setCompaniesState("ready"); })
      .catch(() => { setCompanies(null); setCompaniesState("failed"); });
  };
  // A new row's company is picked from the directory, like every other row's:
  // a Form's Company column takes a directory company, never typed text. The
  // row waits for it while its menu is open.
  const [draftCompany, setDraftCompany] = useState<ItemEditDirectoryOption | null>(null);
  const companyMenuOpen = useRef(false);
  const draftRowRef = useRef<HTMLDivElement | null>(null);
  // What the new row holds as of the last keystroke: a cell's blur and the
  // row's own focusout land in one event, before state catches up.
  const draftLive = useRef<Record<string, string> | null>(null);
  const updateDraft = (next: Record<string, string> | null) => { draftLive.current = next; setDraft(next); };
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[props.category];
  const unit = { manpower: "companies", work_logs: "companies", deliveries: "deliveries", equipment: "items" }[props.category as string] ?? "rows";
  const addLabel = { manpower: "Add company", work_logs: "Add company", deliveries: "Add delivery", equipment: "Add equipment" }[props.category as string] ?? `Add ${definition?.label.toLowerCase() ?? "row"}`;
  const shown = props.rows.filter((row) => !removing.has(row.id));
  const firstTextColumn = columns.find((column) => !NUMERIC_COLUMNS.has(column.columnType ?? "") && column.columnType !== "uid_val");

  const cellFor = (row: ConsoleRow, columnId: string) => props.cells[row.id]?.[columnId];
  const commitCell = (row: ConsoleRow, columnId: string, raw: string, numeric: boolean) => {
    const cell = cellFor(row, columnId);
    if (!cell?.editKey) return;
    const value = numeric ? (raw.trim() === "" ? null : Number(raw)) : raw;
    if (numeric && value !== null && !Number.isFinite(value)) return;
    if (cell.editKey === "title") {
      if (typeof value === "string" && value.trim()) void props.onEdit(row, { title: value.trim() });
      return;
    }
    void props.onEdit(row, { fieldValues: { [cell.editKey]: value } });
  };
  const commitDraft = (next: Record<string, string>) => {
    // A draft row becomes a record on its first value; the binding's column
    // keys are the record's fields.
    const binding = columns;
    const fieldValues: Record<string, string | number | null> = {};
    let title: string | undefined;
    for (const column of binding) {
      const raw = next[column.uid];
      if (raw === undefined || raw.trim() === "") continue;
      const key = draftKeyFor(props.category, column);
      if (!key) continue;
      const numeric = NUMERIC_COLUMNS.has(column.columnType ?? "");
      if (key === "title") title = raw.trim();
      else fieldValues[key] = numeric ? Number(raw) : raw.trim();
      if (!title && column === firstTextColumn) title = raw.trim();
    }
    if (draftCompany) fieldValues.vendor = draftCompany.name;
    if (Object.keys(fieldValues).length === 0 && !title) return;
    void props.onAdd(fieldValues, title ?? draftCompany?.name, draftCompany ?? undefined).then((ok) => {
      if (ok) { updateDraft(null); setDraftCompany(null); }
    });
  };
  // The new row is added when focus leaves it — by any route: a cell, its
  // company, its discard button, Tab or a click — but never while its company
  // list is open, and never for focus moving inside the list.
  const leaveDraft = (event: FocusEvent<HTMLDivElement>) => {
    const row = draftRowRef.current;
    if (!row || !row.contains(event.target as Node) || row.contains(event.relatedTarget as Node | null)) return;
    if (companyMenuOpen.current) return;
    commitDraft(draftLive.current ?? {});
  };

  return (
    <section ref={props.refEl} data-autodesk-section={props.section.id} data-testid="autodesk-report-table" style={card}>
      <div style={cardHead}>
        <h2 style={h2}>{props.section.label}</h2>
        <span style={metaText}>{shown.length} {shown.length === 1 ? unit.replace(/ies$/, "y").replace(/s$/, "") : unit}</span>
      </div>
      <div className="adr-tablewrap"><div className="adr-tableinner">
      <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 4px", padding: "6px 10px 0", alignItems: "center" }}>
        <div />
        {columns.map((column) => (
          <div key={column.uid} style={{ ...eyebrow, color: LABEL, letterSpacing: "1.2px", padding: "6px 8px", textAlign: NUMERIC_COLUMNS.has(column.columnType ?? "") ? "right" : "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={column.label}>{column.label}</div>
        ))}
        <div />
      </div>
      <div style={{ padding: "0 10px 6px" }}>
        {shown.length === 0 && !draft && (
          <div style={{ padding: "10px 8px 8px 32px", fontSize: 13, color: FAINT, borderTop: `1px solid ${ROW_LINE}` }}>No entries recorded</div>
        )}
        {shown.map((row) => (
          <div key={row.id} className="adr-trow" data-item-id={row.id} style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 4px", alignItems: "center", borderTop: `1px solid ${ROW_LINE}` }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 36 }}>
              <SyncMark mark={props.markFor(`row:${row.id}`, props.syncedIds.has(row.id) && !props.fresh.has(row.id))} />
            </span>
            {columns.map((column) => {
              const cell = cellFor(row, column.uid);
              const numeric = NUMERIC_COLUMNS.has(column.columnType ?? "");
              if (column.columnType === "uid_val") {
                const key = cellKeyLabel(column);
                const label = row.autodeskReferences?.[key]?.label ?? (key === "companyId" ? stringValue(row.fieldValues?.vendor) : undefined);
                const isCompany = key === "companyId";
                return (
                  <CompanyCell
                    key={column.uid}
                    label={label}
                    editable={props.editable && isCompany}
                    ariaLabel={column.label}
                    options={companies}
                    state={companiesState}
                    onOpen={loadCompanies}
                    onPick={(option) => void props.onEdit(row, { selectedCompanyRemoteId: option.remoteId, selectedCompanyName: option.name })}
                  />
                );
              }
              return (
                <CellInput
                  key={column.uid}
                  value={displayCell(cell?.value)}
                  numeric={numeric}
                  editable={props.editable && !!cell?.editKey}
                  ariaLabel={column.label}
                  onCommit={(raw) => commitCell(row, column.uid, raw, numeric)}
                />
              );
            })}
            {props.editable ? (
              <button
                type="button"
                className="adr-del"
                aria-label="Remove row"
                title="Remove row"
                onClick={() => {
                  setRemoving((current) => new Set([...current, row.id]));
                  void props.onRemove(row).then((ok) => { if (!ok) setRemoving((current) => { const next = new Set(current); next.delete(row.id); return next; }); });
                }}
                style={{ width: 28, height: 28, borderRadius: 7, border: 0, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: LABEL, justifySelf: "center" }}
              >
                <Trash2 size={13} />
              </button>
            ) : <span />}
          </div>
        ))}
        {draft && (
          <div ref={draftRowRef} onBlur={leaveDraft} className="adr-trow" data-testid="autodesk-report-draft-row" style={{ display: "grid", gridTemplateColumns: gridColumns, gap: "0 4px", alignItems: "center", borderTop: `1px solid ${ROW_LINE}` }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 36 }}><SyncMark mark="absent" /></span>
            {columns.map((column, index) => {
              const numeric = NUMERIC_COLUMNS.has(column.columnType ?? "");
              const key = draftKeyFor(props.category, column);
              if (column.columnType === "uid_val" && cellKeyLabel(column) === "companyId") {
                return (
                  <CompanyCell
                    key={column.uid}
                    label={draftCompany?.name}
                    editable
                    autoOpen={index === 0}
                    ariaLabel={column.label}
                    options={companies}
                    state={companiesState}
                    onOpen={loadCompanies}
                    onOpenChange={(open) => { companyMenuOpen.current = open; }}
                    onPick={(option) => {
                      setDraftCompany(option);
                      // On to the row's next cell; leaving the row adds it.
                      requestAnimationFrame(() => draftRowRef.current?.querySelector<HTMLInputElement>("input:not(:disabled)")?.focus());
                    }}
                  />
                );
              }
              return (
                <CellInput
                  key={column.uid}
                  value={draft[column.uid] ?? ""}
                  numeric={numeric}
                  editable={!!key}
                  autoFocus={index === 0}
                  placeholder={key ? (numeric ? "0" : column.label) : ""}
                  ariaLabel={column.label}
                  onCommit={(raw) => updateDraft({ ...(draftLive.current ?? draft), [column.uid]: raw })}
                />
              );
            })}
            <button type="button" className="adr-del" aria-label="Discard row" title="Discard row" onMouseDown={(event) => event.preventDefault()} onClick={() => { updateDraft(null); setDraftCompany(null); }} style={{ width: 28, height: 28, borderRadius: 7, border: 0, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: LABEL, justifySelf: "center", opacity: 1 }}>
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
      </div></div>
      {props.editable && (
        <button
          type="button"
          className="adr-add"
          onClick={() => updateDraft(draftLive.current ?? draft ?? {})}
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", height: 40, padding: "0 18px", border: 0, borderTop: "1px solid rgba(20,17,12,.06)", background: "transparent", fontSize: 13, fontWeight: 600, color: SUB, cursor: "pointer", textAlign: "left", font: "inherit" }}
        >
          <Plus size={13} strokeWidth={2.2} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{addLabel}</span>
        </button>
      )}
    </section>
  );
}

/** Which record field a draft row's column writes. Directory columns other
 *  than the company cannot be typed; the publisher resolves the company. */
function draftKeyFor(category: ProcoreDailyLogCategory, column: AutodeskTemplateSectionSnapshot["columns"][number]): string | undefined {
  const label = `${column.label} ${column.columnKey ?? ""}`.toLowerCase();
  if (column.columnType === "uid_val") return /company/.test(label) && (category === "manpower" || category === "work_logs") ? "vendor" : undefined;
  if (category === "manpower" || category === "work_logs") {
    if (/worker|headcount/.test(label)) return "workers";
    if (/ot |overtime/.test(label)) return "overtimeHours";
    if (/hour|timespan/.test(label)) return "hours";
    if (/work performed|description|comment/.test(label)) return "comments";
    if (/discipline|trade/.test(label)) return "discipline";
  }
  if (category === "deliveries") {
    if (/material|item/.test(label)) return "contents";
    if (/quantity/.test(label)) return "quantity";
    if (/unit/.test(label)) return "unit";
    if (/comment|description/.test(label)) return "comments";
  }
  if (category === "equipment") {
    if (/equipment|item/.test(label)) return "equipmentName";
    if (/quantity/.test(label)) return "quantity";
    if (/hour|timespan/.test(label)) return "hoursOperating";
    if (/comment|description|note/.test(label)) return "notes";
  }
  return column.columnType === "text_val" ? "comments" : undefined;
}

function cellKeyLabel(column: AutodeskTemplateSectionSnapshot["columns"][number]): string {
  return /role/i.test(`${column.label} ${column.columnKey ?? ""}`) ? "roleId" : "companyId";
}

function CellInput({ value, numeric, editable, ariaLabel, onCommit, placeholder, autoFocus }: {
  value: string;
  numeric: boolean;
  editable: boolean;
  ariaLabel: string;
  onCommit: (raw: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState(value);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setText(value); }, [value]);
  return (
    <input
      className={`adr-cell${numeric ? " num" : ""}`}
      type="text"
      inputMode={numeric ? "decimal" : undefined}
      value={text}
      placeholder={placeholder ?? (editable ? (numeric ? "0" : "") : "")}
      disabled={!editable}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      onFocus={() => { focused.current = true; }}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        focused.current = false;
        if (text !== value) onCommit(text);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        if (event.key === "Escape") { setText(value); (event.target as HTMLInputElement).blur(); }
      }}
    />
  );
}

type CompanyDirectoryState = "idle" | "loading" | "ready" | "failed";

function CompanyCell({ label, editable, ariaLabel, options, state, onOpen, onPick, autoOpen = false, onOpenChange }: {
  label?: string;
  editable: boolean;
  ariaLabel: string;
  options: ItemEditDirectoryOption[] | null;
  state: CompanyDirectoryState;
  onOpen: () => void;
  onPick: (option: ItemEditDirectoryOption) => void;
  /** A new row opens on its company. */
  autoOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const [query, setQuery] = useState("");
  const setOpen = (next: boolean) => { setOpenState(next); onOpenChange?.(next); if (!next) setQuery(""); };
  useEffect(() => {
    if (!autoOpen) return;
    onOpen();
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const matches = (options ?? []).filter((option) => option.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 40);
  const pick = (option: ItemEditDirectoryOption) => { setOpen(false); if (option.name !== label) onPick(option); };
  const status = state === "loading" || state === "idle"
    ? "Loading companies…"
    : state === "failed"
      ? "The Autodesk company directory could not be loaded."
      : !options?.length
        ? "No companies in the Autodesk directory."
        : matches.length === 0
          ? "No matching companies"
          : null;
  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <button
        type="button"
        className={editable ? "adr-cell" : "adr-cell"}
        disabled={!editable}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { onOpen(); setOpen(!open); }}
        style={{ textAlign: "left", cursor: editable ? "pointer" : "default", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: label ? INK : FAINT }}
      >
        {label ?? (editable ? "Company" : "—")}
      </button>
      {open && (
        <Menu onClose={() => setOpen(false)} style={{ left: 0, top: 38, width: 280 }}>
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && matches[0]) { event.preventDefault(); pick(matches[0]); }
            }}
            placeholder="Search companies"
            aria-label="Search companies"
            className="adr-cell"
            style={{ background: RECESS, marginBottom: 4 }}
          />
          {status && <div data-testid="autodesk-company-status" style={{ padding: "8px 10px", fontSize: 13, color: FAINT }}>{status}</div>}
          {matches.map((option) => (
            <MenuItem key={option.remoteId} on={option.name === label} label={option.name} onPick={() => pick(option)} />
          ))}
        </Menu>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jobsite events (template questions)
// ---------------------------------------------------------------------------

type Group = AutodeskDailyLogDisplayGroup & { rows: ConsoleRow[] };

function EventsCard(props: {
  refEl: (element: HTMLElement | null) => void;
  label: string;
  groups: Group[];
  manual: Group[];
  saved?: AutodeskSavedForm;
  answerFor: (group: Group) => string[];
  editable: boolean;
  markFor: (key: string, inAutodesk: boolean) => "syncing" | "synced" | "absent";
  inAutodesk: boolean;
  jobSiteId: string;
  onAnswer: (group: Group, value: string | string[]) => unknown;
  onSelectEntry: (id: string) => void;
}) {
  const yesCount = props.groups.filter((group) => isAffirmative(group, props.answerFor(group))).length;
  return (
    <section ref={props.refEl} data-autodesk-section="questions" data-testid="autodesk-report-events" style={card}>
      <div style={cardHead}><h2 style={h2}>{props.label}</h2><span style={metaText}>{yesCount} of {props.groups.length} yes</span></div>
      {props.groups.map((group, index) => {
        const answer = props.answerFor(group);
        const affirmative = isAffirmative(group, answer);
        return (
          <div key={group.id} data-autodesk-question={group.fieldId} style={{ padding: "12px 18px", borderTop: index === 0 ? "none" : `1px solid ${ROW_LINE}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, flex: "none" }}>
                <SyncMark mark={props.markFor(`q:${group.fieldId}`, props.inAutodesk)} />
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500 }}>
                {group.label}
              </span>
              {group.valueKind === "toggle" ? (
                <YesNo value={answer[0]} editable={props.editable} label={group.label} onPick={(value) => props.onAnswer(group, value)} />
              ) : (
                <ChoiceMenu group={group} value={answer} editable={props.editable} onPick={(value) => props.onAnswer(group, value)} />
              )}
            </div>
            {affirmative && group.rows.map((row) => (
              <EventEntry key={row.id} row={row} jobSiteId={props.jobSiteId} onSelect={props.onSelectEntry} />
            ))}
          </div>
        );
      })}
    </section>
  );
}

function YesNo({ value, editable, label, onPick }: { value?: string; editable: boolean; label: string; onPick: (value: "Yes" | "No") => void }) {
  const option = (choice: "Yes" | "No") => {
    const on = value === choice;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        disabled={!editable}
        onClick={() => { if (!on) onPick(choice); }}
        style={{ height: 26, padding: "0 12px", borderRadius: 6, border: 0, background: on ? ADR.paper : "transparent", color: on ? INK : SUB, fontSize: 12.5, fontWeight: 600, cursor: editable ? "pointer" : "default", boxShadow: on ? "0 1px 2px rgba(20,17,12,.12)" : "none", font: "inherit" }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{choice}</span>
      </button>
    );
  };
  return (
    <span role="radiogroup" aria-label={label} style={{ display: "inline-flex", padding: 2, borderRadius: 8, background: MENU_ON, flex: "none" }}>
      {option("Yes")}
      {option("No")}
    </span>
  );
}

function ChoiceMenu({ group, value, editable, onPick }: { group: Group; value: string[]; editable: boolean; onPick: (value: string | string[]) => void }) {
  const [open, setOpen] = useState(false);
  const multi = group.valueKind === "multi_choice";
  const options = (group.presets ?? []).filter((preset) => !["N/A"].includes(preset) || group.valueKind !== "toggle");
  const label = value.length === 0 ? "Not answered" : value.join(", ");
  return (
    <span style={{ position: "relative", flex: "none" }}>
      <button
        type="button"
        disabled={!editable}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={group.label}
        onClick={() => setOpen((current) => !current)}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 30, maxWidth: 280, padding: "0 10px 0 12px", borderRadius: 8, border: "1px solid rgba(20,17,12,.12)", background: ADR.card, fontSize: 12.5, fontWeight: 600, color: value.length ? INK : FAINT, cursor: editable ? "pointer" : "default", font: "inherit" }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        {editable && <ChevronDown size={13} color={SUB} aria-hidden />}
      </button>
      {open && (
        <Menu onClose={() => setOpen(false)} style={{ right: 0, top: 36, width: 280 }} role="listbox" multi={multi}>
          {options.map((option) => {
            const on = value.includes(option);
            return (
              <MenuItem
                key={option}
                on={on}
                label={option}
                onPick={() => {
                  if (!multi) { setOpen(false); if (!on) onPick(option); return; }
                  onPick(on ? value.filter((entry) => entry !== option) : [...value, option]);
                }}
              />
            );
          })}
        </Menu>
      )}
    </span>
  );
}

function EventEntry({ row, jobSiteId, onSelect }: { row: ConsoleRow; jobSiteId: string; onSelect: (id: string) => void }) {
  const bullets = entryBullets(row);
  const hasPhoto = !!row.thumbnailUrl;
  return (
    <div style={{ marginTop: 10, animation: "adr-fade .15s both" }}>
      <div style={{ display: "grid", gridTemplateColumns: hasPhoto ? "minmax(0,1fr) 150px" : "minmax(0,1fr)", gap: 12 }}>
        <div style={{ background: RECESS, border: "1px solid rgba(20,17,12,.06)", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ ...eyebrow, fontSize: 8.5, letterSpacing: "1.2px" }}>Note</div>
          <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: 13, lineHeight: 1.55, color: INK }}>
            {bullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
          </ul>
        </div>
        {hasPhoto && (
          <div style={{ background: RECESS, border: "1px solid rgba(20,17,12,.06)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ ...eyebrow, fontSize: 8.5, letterSpacing: "1.2px" }}>Photos · {row.photoCount ?? 1}</span>
            <span style={{ width: 52, height: 52, borderRadius: 7, overflow: "hidden", display: "block" }}>
              <EvidenceTile jobSiteId={jobSiteId} url={row.thumbnailUrl} height={52} alt="" />
            </span>
          </div>
        )}
      </div>
      <EntryLink row={row} onSelect={onSelect} style={{ marginTop: 8 }} />
    </div>
  );
}

function EntryLink({ row, onSelect, style }: { row: ConsoleRow; onSelect: (id: string) => void; style?: CSSProperties }) {
  return (
    <button
      type="button"
      className="adr-from"
      onClick={() => onSelect(row.id)}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: SUB, background: "transparent", border: 0, padding: 0, cursor: "pointer", font: "inherit", textAlign: "left", ...style }}
    >
      <span style={{ fontSize: 12 }}>From <span style={{ fontWeight: 600, color: INK }}>{row.title}</span></span>
      <span style={{ ...mono, fontSize: 9, fontWeight: 600, letterSpacing: ".6px", color: FAINT }}>{shortTime(row.createdAt).toUpperCase()}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: ADR.green }}><Check size={12} color={ADR.green} strokeWidth={2.6} />Accepted</span>
      <ChevronRight size={14} color={FAINT} aria-hidden />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Notes, signature, other manual fields
// ---------------------------------------------------------------------------

function TextCard({ refEl, title, text, editable, placeholder, onCommit }: {
  refEl: (element: HTMLElement | null) => void;
  title: string;
  text: string;
  editable: boolean;
  placeholder: string;
  onCommit: (text: string) => unknown;
}) {
  return (
    <section ref={refEl} data-autodesk-section="notes" style={card}>
      <div style={cardHead}><h2 style={h2}>{title}</h2></div>
      <div style={{ padding: "12px 18px 16px" }}>
        <EditableText
          testId="autodesk-report-notes"
          text={text}
          editable={editable}
          placeholder={placeholder}
          onCommit={onCommit}
          style={{ margin: "0 -6px", padding: "4px 6px", fontSize: 14, lineHeight: 1.55, textWrap: "pretty", minHeight: 48, whiteSpace: "pre-wrap" } as CSSProperties}
        />
      </div>
    </section>
  );
}

function ManualCard({ refEl, section, groups, saved, formUrl: url }: {
  refEl: (element: HTMLElement | null) => void;
  section: AutodeskTemplateSectionSnapshot;
  groups: Group[];
  saved?: AutodeskSavedForm;
  formUrl: string | null;
}) {
  const fields = groups.length > 0 ? groups : section.fields.filter((field) => field.type === "question").map((field) => ({ id: field.uid, label: field.label, fieldId: field.schema ?? field.uid }) as unknown as Group);
  return (
    <section ref={refEl} data-autodesk-section={section.kind} style={card}>
      {fields.map((group, index) => {
        const value = saved?.questions.find((answer) => answer.fieldId === group.fieldId);
        const signed = value?.signatureRecorded;
        const status = signed ? (value?.name ? `Signed by ${value.name}` : "Signed") : value ? savedAutodeskDisplayValue(value) : "Not signed";
        return (
          <div key={group.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderTop: index === 0 ? "none" : `1px solid ${ROW_LINE}` }}>
            <h2 style={h2}>{group.label}</h2>
            <span style={{ fontSize: 13, color: signed ? ADR.green : LABEL }}>{status === "Not recorded" ? "Not signed" : status}</span>
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="adr-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: INK, whiteSpace: "nowrap", textDecoration: "none" }}>
                {signed ? "View in Autodesk" : "Sign in Autodesk"} <ExternalLink size={13} color={SUB} aria-hidden />
              </a>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Menus
// ---------------------------------------------------------------------------

/**
 * A dropdown that no card can clip. The report's cards clip their own
 * overflow (rounded corners, sideways-scrolling tables), so a menu drawn
 * inside one was cut off at the card's edge. The menu is drawn on the page
 * instead, at a fixed position measured from the control that opened it: the
 * caller's `top`/`left`/`right` are offsets from that control's box, as they
 * were when the menu sat inside it. Near the bottom of the window it opens
 * upward rather than running off screen.
 */
function Menu({ children, onClose, style = {}, role = "listbox", multi = false }: { children: ReactNode; onClose: () => void; style?: CSSProperties; role?: string; multi?: boolean }) {
  const marker = useRef<HTMLSpanElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const host = marker.current?.parentElement;
    if (!host) return;
    const measure = () => setAnchor(host.getBoundingClientRect());
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, []);
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menu.current?.contains(target) || marker.current?.parentElement?.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [onClose]);
  const { top, left, right, width, ...rest } = style;
  const menuWidth = typeof width === "number" ? width : 280;
  let placement: CSSProperties = {};
  if (anchor) {
    const gutter = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const x = right !== undefined
      ? anchor.right - Number(right) - menuWidth
      : anchor.left + Number(left ?? 0);
    const below = anchor.top + (top !== undefined ? Number(top) : anchor.height + 6);
    const roomBelow = viewportHeight - below - gutter;
    const roomAbove = anchor.top - gutter - 6;
    const upward = roomBelow < 180 && roomAbove > roomBelow;
    placement = {
      left: Math.max(gutter, Math.min(x, viewportWidth - menuWidth - gutter)),
      width: menuWidth,
      ...(upward
        ? { bottom: viewportHeight - anchor.top + 6, maxHeight: Math.min(320, roomAbove) }
        : { top: below, maxHeight: Math.min(320, roomBelow) }),
    };
  }
  return (
    <>
      <span ref={marker} hidden />
      {anchor && createPortal(
        <div ref={menu} role={role} aria-multiselectable={multi || undefined} data-testid="autodesk-report-menu" style={{ position: "fixed", zIndex: 1000, overflowY: "auto", background: ADR.paper, border: "1px solid rgba(20,17,12,.14)", borderRadius: 10, boxShadow: "0 14px 30px rgba(22,18,10,.18)", padding: 6, animation: "adr-fade .12s both", ...rest, ...placement, maxHeight: placement.maxHeight }}>
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ label, on = false, onPick }: { label: ReactNode; on?: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={on}
      className="adr-menu-item"
      onClick={onPick}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 34, padding: "6px 10px", border: 0, borderRadius: 7, background: on ? MENU_ON : "transparent", fontSize: 13.5, color: INK, cursor: "pointer", textAlign: "left", font: "inherit" }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>{label}</span>
      {on && <Check size={14} color={INK} aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function formUrl(template: DailyLogAutodeskTemplate): string | null {
  if (!template.projectId || !template.templateId || !template.formId) return null;
  return `https://acc.autodesk.com/build/forms/projects/${encodeURIComponent(template.projectId)}/field-reports/${encodeURIComponent(template.templateId)}/reports/${encodeURIComponent(template.formId)}`;
}

/** "Yes", or any choice other than the question's "nothing happened" one. */
export function isAffirmative(group: { valueKind?: string }, answer: string[]): boolean {
  if (group.valueKind === "toggle") return answer[0] === "Yes";
  return answer.some((value) => value && !/^(no|none)\b/i.test(value));
}

function savedAnswer(value: AutodeskSavedValue): string[] | null {
  if (value.unavailable) return null;
  if (value.toggleVal !== undefined) {
    const normalized = String(value.toggleVal).toLowerCase();
    return [normalized === "true" || normalized === "yes" ? "Yes" : normalized === "false" || normalized === "no" ? "No" : String(value.toggleVal)];
  }
  if (value.arrayVal) return value.arrayVal;
  if (value.choiceVal) return [value.choiceVal];
  if (value.textVal) return [value.textVal];
  return null;
}

function displayCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/** The note a jobsite-event entry contributes: its filled fields, then its
 *  narrative — the same facts the publisher writes into the answer's notes. */
export function entryBullets(row: ConsoleRow): string[] {
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[row.category as ProcoreDailyLogCategory];
  const values = row.fieldValues ?? {};
  const long = new Set(["comments", "comment", "description", "details", "notes"]);
  const bullets: string[] = [];
  for (const field of definition?.fields ?? []) {
    if (long.has(field.key)) continue;
    const raw = values[field.key];
    if (raw === undefined || raw === null || raw === "" || typeof raw === "boolean") continue;
    bullets.push(`${field.label}: ${String(raw)}`);
  }
  const narrative = [...long].map((key) => stringValue(values[key])).find(Boolean) ?? stringValue(row.description) ?? stringValue(row.summary);
  if (narrative) bullets.push(narrative);
  if (bullets.length === 0) bullets.push(row.title);
  return bullets;
}

function splitPath(option: ItemEditDirectoryOption): { parent: string; name: string } {
  const parts = (option.path ?? option.name).split(/\s*[>›/]\s*/).filter(Boolean);
  if (parts.length <= 1) return { parent: "", name: option.name };
  // The nearest parent places a location ("Level 02 › Hallway"); the full
  // chain back to the project root is noise at this size.
  return { parent: parts[parts.length - 2]!, name: parts[parts.length - 1]! };
}

function shortTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")}${hours < 12 ? "a" : "p"}`;
}

function longDate(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year!, (month ?? 1) - 1, date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
}

function monthDay(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year!, (month ?? 1) - 1, date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function hourLabel(hour: string): string {
  const match = /T?(\d{2}):(\d{2})/.exec(hour);
  return match ? `${match[1]}:${match[2]}` : hour;
}

/** Morning, midday and afternoon — the three readings a superintendent
 *  actually compares — from Autodesk's hourly list. */
export function pickWeatherHours<T extends { hour: string }>(hours: T[]): T[] {
  if (hours.length <= 3) return hours;
  const hourOf = (entry: T) => Number(/T?(\d{2}):/.exec(entry.hour)?.[1] ?? NaN);
  const picks: T[] = [];
  for (const target of [7, 12, 16]) {
    const best = [...hours].sort((a, b) => Math.abs(hourOf(a) - target) - Math.abs(hourOf(b) - target))[0];
    if (best && !picks.includes(best)) picks.push(best);
  }
  return picks.sort((a, b) => hourOf(a) - hourOf(b));
}

function weatherIcon(summary: string) {
  const text = summary.toLowerCase();
  if (/thunder|lightning|storm/.test(text)) return CloudLightning;
  if (/snow|sleet|flurr/.test(text)) return CloudSnow;
  if (/drizzle/.test(text)) return CloudDrizzle;
  if (/rain|shower/.test(text)) return CloudRain;
  if (/fog|mist|haze/.test(text)) return CloudFog;
  if (/clear|sunny/.test(text)) return Sun;
  if (/partly|mostly cloudy|mostly sunny/.test(text)) return CloudSun;
  return Cloud;
}
